/**
 * Shared system_name token validator for values that become dynamic field
 * paths (Elasticsearch) or Gremlin edge labels (perspective + relationship
 * system names). Both graph-service and search-service must reject
 * non-conforming tokens before using them this way — never-widen: an
 * invalid token must be treated by callers as "no match", never as "skip
 * the check" (which would silently broaden the candidate set).
 */
const SYSTEM_NAME_TOKEN_PATTERN = /^[a-z][a-z0-9_]*$/;

export function isValidSystemNameToken(value: string): boolean {
  return typeof value === 'string' && SYSTEM_NAME_TOKEN_PATTERN.test(value);
}

/**
 * The only directions a RelationshipFilter may carry. Route schemas enforce this
 * enum at the API boundary, but STORED filters (context definitions, approach
 * applicability, monitoring rules) bypass those schemas — and the platform's own
 * relationship API speaks 'outgoing' | 'incoming' | 'both', so an author writing
 * `direction: 'outgoing'` into a stored filter is a live scenario.
 *
 * Every engine must validate against this list rather than falling through to a
 * default: a silent fallthrough picked 'both' in Gremlin and 'target' in
 * Elasticsearch, so one stored filter yielded two different result sets, and the
 * Gremlin side was strictly wider than the author's intent (never-widen violation).
 * An unrecognized direction must match nothing, exactly like an invalid system name.
 */
export const RELATIONSHIP_DIRECTIONS = ['source', 'target', 'both'] as const;

export type RelationshipDirection = (typeof RELATIONSHIP_DIRECTIONS)[number];

/** True when `value` is undefined (defaults to 'both') or an exact enum member. */
export function isValidRelationshipDirection(value: unknown): value is RelationshipDirection | undefined {
  if (value === undefined) return true;
  return typeof value === 'string' && (RELATIONSHIP_DIRECTIONS as readonly string[]).includes(value);
}

/**
 * The only selectors an ArcFilter may carry. Route schemas enforce this enum
 * at the API boundary, but STORED filters (context definitions, monitoring
 * rules, arc conditions) bypass those schemas, so every engine must validate
 * against this list rather than falling through to a default — an
 * unrecognized selector must match nothing, exactly like an invalid system
 * name (never-widen).
 */
export const ARC_SELECTORS = ['any', 'primary'] as const;

export type ArcSelector = (typeof ARC_SELECTORS)[number];

/** True when `value` is undefined (defaults to 'any') or an exact enum member. */
export function isValidArcSelector(value: unknown): value is ArcSelector | undefined {
  if (value === undefined) return true;
  return typeof value === 'string' && (ARC_SELECTORS as readonly string[]).includes(value);
}
